import React from 'react';
import PeopleIcon from '@mui/icons-material/People';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import NotificationsIcon from '@mui/icons-material/Notifications';
import EmailIcon from '@mui/icons-material/Email';
import SequenceIcon from '@mui/icons-material/MoveDownTwoTone';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import TerminalIcon from '@mui/icons-material/Terminal';
import WorkIcon from '@mui/icons-material/Work';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import HandshakeIcon from '@mui/icons-material/Handshake';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import DisabledByDefaultIcon from '@mui/icons-material/DisabledByDefault';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import HubIcon from '@mui/icons-material/Hub';
import { CalendarIcon } from '@mui/x-date-pickers/icons';
import type { SectionItem } from './SettingsSection';

/** Expanded width (px) of the main app drawer and settings index sidebar. Sync: ResponsiveDrawer, AuthLayout, SettingsShell. */
export const EXPANDED_SIDEBAR_WIDTH_PX = 210;

const iconSize = 'small' as const;

export function profileNavItems(): SectionItem[] {
  return [
    {
      title: 'My Account',
      link: '/settings/account',
      icon: <AccountCircleIcon fontSize={iconSize} />,
      testId: '@settings-account',
    },
    {
      title: 'Notifications',
      link: '/settings/notifications',
      icon: <NotificationsIcon fontSize={iconSize} />,
      testId: '@settings-notifications',
    },
  ];
}

export function outreachNavItems(): SectionItem[] {
  return [
    {
      title: 'Sender Accounts',
      link: '/settings/sender-accounts',
      icon: <ContactMailIcon fontSize={iconSize} />,
      testId: '@settings-sender-accounts',
    },
    {
      title: 'Email Providers',
      link: '/settings/email-retrieval-api',
      roles: ['superAdmin', 'admin'],
      icon: <TerminalIcon fontSize={iconSize} />,
    },
  ];
}

export function workplaceNavItems(): SectionItem[] {
  return [
    {
      title: 'Candidates',
      link: '/settings/candidates',
      icon: <SupervisedUserCircleIcon fontSize={iconSize} />,
      testId: '@settings-candidates',
      submenuKey: 'candidates',
      subItems: [
        { title: 'Statuses', link: '/settings/candidates', testId: '@settings-candidates-statuses' },
        {
          title: 'Custom Fields',
          link: '/settings/candidates?tab=1',
          testId: '@settings-candidates-custom-fields',
        },
        {
          title: 'Phone Call Recording',
          link: '/settings/candidates?tab=2',
          testId: '@settings-candidates-transcription',
        },
        {
          title: 'Google/Zoom Transcript',
          link: '/settings/candidates?tab=5',
          testId: '@settings-candidates-google-zoom-transcription',
        },
        {
          title: 'Transcript Prompts',
          link: '/settings/candidates?tab=3',
          testId: '@settings-candidates-transcription-prompts',
        },
        {
          title: 'Extension',
          link: '/settings/candidates?tab=4',
          testId: '@settings-candidates-extension',
        },
      ],
    },
    {
      title: 'Jobs',
      link: '/settings/jobs',
      icon: <WorkIcon fontSize={iconSize} />,
      testId: '@settings-jobs',
      submenuKey: 'jobs',
      subItems: [
        { title: 'Statuses', link: '/settings/jobs', testId: '@settings-jobs-statuses' },
        { title: 'Custom Fields', link: '/settings/jobs?tab=1', testId: '@settings-jobs-custom-fields' },
        { title: 'Job Board', link: '/settings/jobs?tab=2', testId: '@settings-jobs-job-board' },
        { title: 'Analytics', link: '/settings/jobs?tab=3', testId: '@settings-jobs-analytics' },
      ],
    },
    {
      title: 'Clients',
      link: '/settings/clients',
      icon: <HandshakeIcon fontSize={iconSize} />,
      testId: '@settings-clients',
      submenuKey: 'clients',
      subItems: [
        { title: 'Statuses', link: '/settings/clients', testId: '@settings-clients-statuses' },
        { title: 'Custom Fields', link: '/settings/clients?tab=1', testId: '@settings-clients-custom-fields' },
        {
          title: 'Lead-Gen AI',
          link: '/settings/clients?tab=2',
          testId: '@settings-clients-lead-generation-prompt',
          roles: ['superAdmin'],
        },
        {
          title: 'Invoices',
          link: '/settings/clients?tab=3',
          testId: '@settings-clients-invoice-settings',
          roles: ['superAdmin'],
        },
      ],
    },
    {
      title: 'Integrations',
      link: '/settings/integrations',
      icon: <HubIcon fontSize={iconSize} />,
      testId: '@settings-integrations',
    },
    {
      title: 'Booking Links',
      link: '/settings/booking-links',
      icon: <CalendarIcon fontSize={iconSize} />,
      testId: 'booking-links',
    },
  ];
}

export function templatesNavItems(): SectionItem[] {
  return [
    {
      title: 'Sequences',
      link: '/settings/sequence-templates',
      icon: <SequenceIcon fontSize={iconSize} />,
    },
    {
      title: 'Emails',
      link: '/settings/email-templates',
      icon: <EmailIcon fontSize={iconSize} />,
    },
    {
      title: 'Notes',
      link: '/settings/note-templates',
      icon: <TextSnippetIcon fontSize={iconSize} />,
    },
  ];
}

export function otherNavItems(): SectionItem[] {
  return [
    {
      title: 'Team Members',
      link: '/settings/team',
      icon: <PeopleIcon fontSize={iconSize} />,
    },
    {
      title: 'Billing',
      link: '/settings/billing',
      icon: <CreditCardIcon fontSize={iconSize} />,
      roles: ['superAdmin'],
    },
  ];
}

export function dataImportNavItems(): SectionItem[] {
  return [
    {
      title: 'Mass Import',
      link: '/settings/mass-import',
      icon: <TextSnippetIcon fontSize={iconSize} />,
      testId: '@settings-mass-import',
    },
    {
      title: 'White Glove Import',
      link: '/settings/migrate-data',
      icon: <CloudDownloadIcon fontSize={iconSize} />,
    },
  ];
}

export function dangerZoneNavItems(): SectionItem[] {
  return [
    {
      title: 'Account Backup',
      link: '/settings/backup',
      roles: ['superAdmin', 'admin'],
      icon: <CloudUploadIcon fontSize={iconSize} />,
    },
    {
      title: 'Delete Account',
      link: '/settings/delete-account',
      testId: '@settings-delete-account',
      roles: ['superAdmin'],
      icon: <DisabledByDefaultIcon fontSize={iconSize} />,
    },
  ];
}
